import { useState } from "react";
import MarketingDocGate from "../MarketingDocGate";
import { lengthError } from "../../utils/validation";

function ContextualGreeting({ initial = {}, onSave }) {
  const [isActive, setIsActive] = useState(initial.isActive ?? true);
  const [greetingName, setGreetingName] = useState(
    initial.greetingName ?? "Pricing Page Greeting"
  );

  const [triggerType, setTriggerType] = useState(initial.triggerType ?? "URL contains");

  const [pageRule, setPageRule] = useState(initial.pageRule ?? "/pricing");

  const [message, setMessage] = useState(
    initial.message ?? "Hi! 👋 Looking for pricing information? I can help you compare our plans."
  );

  const [cta, setCta] = useState(initial.cta ?? "View Pricing");

  const [showPreview, setShowPreview] = useState(false);
  const [formErrors, setFormErrors] = useState([]);

  const handleSave = async (event) => {
    event.preventDefault();

    const problems = [
      lengthError(greetingName, { min: 2, max: 60, label: "Greeting name" }),
      lengthError(pageRule, { min: 1, max: 200, label: "Page rule" }),
      lengthError(message, { min: 5, max: 300, label: "Greeting message" }),
      lengthError(cta, { max: 40, label: "CTA text" })
    ].filter(Boolean);
    setFormErrors(problems);
    if (problems.length) return;

    try {
      await onSave({ isActive, greetingName, triggerType, pageRule, message, cta });
      alert("Contextual greeting saved successfully!");
    } catch (error) {
      alert(error.message || "Unable to save the greeting.");
    }
  };

  return (
    <div className="dashboard-page">

      {/* Header */}

      <div className="page-header">

        <div>
          <h2>Contextual Greetings</h2>

          <p>
            Create personalized greetings based on visitor context
            and page rules.
          </p>
        </div>

        <div className="header-actions">

          <button
            className="btn btn-outline-primary me-2"
            onClick={() => setShowPreview(true)}
          >
            Preview
          </button>

          <button
            className="btn btn-primary"
            onClick={handleSave}
          >
            Save Greeting
          </button>

        </div>

      </div>


      <div className="row g-4">

        {/* Main configuration */}

        <div className="col-lg-8">

          <div className="card greeting-card">

            <div className="card-header bg-white">

              <h5 className="mb-1">
                Contextual Greeting Configuration
              </h5>

              <small className="text-muted">
                Configure when and how the greeting should appear.
              </small>

            </div>


            <div className="card-body">

              <form onSubmit={handleSave} noValidate>

                {formErrors.length > 0 && (
                  <div className="alert alert-danger" role="alert">
                    <ul className="mb-0 ps-3">{formErrors.map((m) => <li key={m}>{m}</li>)}</ul>
                  </div>
                )}

                {/* Greeting Name */}

                <div className="mb-4">

                  <label className="form-label fw-semibold" htmlFor="cg-name">
                    Greeting Name
                  </label>

                  <input
id="cg-name"
                                        type="text"
                    className="form-control"
                    value={greetingName}
                    onChange={(e) =>
                      setGreetingName(e.target.value)
                    }
                    placeholder="Example: Pricing Page Greeting"
                  />

                  <div className="form-text">
                    Give this greeting a name so your team can
                    identify it easily.
                  </div>

                </div>


                {/* Status */}

                <div className="mb-4">

                  <label className="form-label fw-semibold">
                    Status
                  </label>

                  <div className="status-toggle">

                    <div>
                      <span
                        className={
                          isActive
                            ? "status-dot active"
                            : "status-dot inactive"
                        }
                      ></span>

                      {isActive ? "Active" : "Inactive"}
                    </div>

                    <div className="form-check form-switch">

                      <input
                        className="form-check-input"
                        type="checkbox"
                        role="switch"
                        aria-label="Greeting is active"
                        checked={isActive}
                        onChange={() =>
                          setIsActive(!isActive)
                        }
                      />

                    </div>

                  </div>

                </div>


                {/* Trigger */}

                <div className="mb-4">

                  <label className="form-label fw-semibold">
                    Trigger / Page Rule
                  </label>

                  <div className="row g-2">

                    <div className="col-md-4">

                      <select
                        className="form-select"
                        value={triggerType}
                        aria-label="Trigger type"
                        onChange={(e) =>
                          setTriggerType(e.target.value)
                        }
                      >

                        <option>URL contains</option>
                        <option>URL equals</option>
                        <option>Page starts with</option>
                        <option>All pages</option>

                      </select>

                    </div>

                    <div className="col-md-8">

                      <input
                        type="text"
                        className="form-control"
                        value={pageRule}
                        aria-label="Page rule"
                        onChange={(e) =>
                          setPageRule(e.target.value)
                        }
                        placeholder="/pricing"
                      />

                    </div>

                  </div>

                  <div className="form-text">
                    Define the page or visitor context where
                    this greeting should appear.
                  </div>

                </div>


                {/* Greeting Message */}

                <div className="mb-4">

                  <label className="form-label fw-semibold" htmlFor="cg-message">
                    Greeting Message
                  </label>

                  <textarea
id="cg-message"
                                        className="form-control"
                    rows="4"
                    maxLength="300"
                    value={message}
                    onChange={(e) =>
                      setMessage(e.target.value)
                    }
                    placeholder="Enter your greeting message..."
                  ></textarea>

                  <div className="text-end small text-muted mt-1">
                    {message.length}/300
                  </div>

                </div>


                {/* CTA */}

                <div className="mb-4">

                  <label className="form-label fw-semibold" htmlFor="cg-cta">
                    Call To Action
                  </label>

                  <select
id="cg-cta"
                                        className="form-select"
                    value={cta}
                    onChange={(e) =>
                      setCta(e.target.value)
                    }
                  >

                    <option>View Pricing</option>
                    <option>Book a Demo</option>
                    <option>Start Free Trial</option>
                    <option>Talk to Support</option>
                    <option>No CTA</option>

                  </select>

                </div>


                {/* Buttons */}

                <div className="d-flex justify-content-end gap-2">

                  <button
                    type="button"
                    className="btn btn-light border"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="btn btn-primary px-4"
                  >
                    Save Greeting
                  </button>

                </div>

              </form>

            </div>

          </div>

        </div>


        {/* Right side preview */}

        <div className="col-lg-4">

          <div className="card preview-card">

            <div className="card-header bg-white">

              <h5 className="mb-1">
                Live Preview
              </h5>

              <small className="text-muted">
                Preview how visitors will see the greeting.
              </small>

            </div>


            <div className="card-body">

              <div className="chat-preview">

                <div className="chat-header">

                  <div className="bot-avatar">
                    🤖
                  </div>

                  <div>
                    <strong>ChatBizz AI</strong>

                    <small>
                      Online
                    </small>
                  </div>

                </div>


                <div className="chat-message">

                  <p>
                    {message ||
                      "Your greeting message will appear here."}
                  </p>

                  {cta !== "No CTA" && (
                    <button className="btn btn-primary btn-sm">
                      {cta}
                    </button>
                  )}

                </div>

                <div className="chat-input">
                  Type your message...
                </div>

              </div>

            </div>

          </div>


          {/* Rule summary */}

          <div className="card mt-4">

            <div className="card-body">

              <h6 className="fw-bold">
                Rule Summary
              </h6>

              <div className="summary-row">

                <span>Status</span>

                <span
                  className={
                    isActive
                      ? "badge bg-success-subtle text-success"
                      : "badge bg-secondary-subtle text-secondary"
                  }
                >
                  {isActive ? "Active" : "Inactive"}
                </span>

              </div>

              <div className="summary-row">

                <span>Trigger</span>

                <strong>
                  {triggerType}
                </strong>

              </div>

              <div className="summary-row">

                <span>Page</span>

                <strong>
                  {pageRule || "Not configured"}
                </strong>

              </div>

              <div className="summary-row">

                <span>CTA</span>

                <strong>
                  {cta}
                </strong>

              </div>

            </div>

          </div>

        </div>

      </div>


      {/* Preview Modal */}

      {showPreview && (

        <div className="preview-overlay">

          <div className="preview-modal">

            <div className="d-flex justify-content-between mb-3">

              <h5>
                Visitor Preview
              </h5>

              <button
                className="btn-close"
                onClick={() => setShowPreview(false)}
              ></button>

            </div>

            <div className="chat-preview">

              <div className="chat-header">

                <div className="bot-avatar">
                  🤖
                </div>

                <div>
                  <strong>ChatBizz AI</strong>

                  <small>Online</small>
                </div>

              </div>

              <div className="chat-message">

                <p>{message}</p>

                {cta !== "No CTA" && (
                  <button className="btn btn-primary btn-sm">
                    {cta}
                  </button>
                )}

              </div>

            </div>

          </div>

        </div>

      )}

    </div>
  );
}

export default function ContextualGreetingPage() {
  return <MarketingDocGate section="greeting">{({ doc, save }) => <ContextualGreeting initial={doc} onSave={save} />}</MarketingDocGate>;
}